import { useState, useEffect, useMemo, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NspireWalkthrough — Room-by-room guided NSPIRE / Section 8 inspection.
 *
 * Sits inside the Design Studio NSPIRE tab. Bound to the property
 * address entered above so the walkthrough auto-attaches to the same
 * variation + procurement ledger for that unit. Every submission
 * rolls up into the institutional entity's corporate Resolute
 * closeout packet via /api/admin/closeout/{entity_id}/nspire.
 *
 * Props:
 *   propertyAddress — required. If empty, the component prompts the
 *                     user to enter an address in the Design Studio
 *                     header first.
 *   entityId        — institutional owner / GP id (optional, defaults
 *                     to "unassigned"). Wired later from Dashboard.
 *   inspector       — display name of the field inspector (optional).
 */

const STATUS = ["pass", "fail", "na"];
const STATUS_LABEL = { pass: "Pass", fail: "Fail", na: "N/A" };
const STATUS_COLOR = {
  pass: "#00DC78",
  fail: "#F87171",
  na:   "#A0A0A0",
};
const SEVERITY_BADGE = {
  life_safety: { text: "LIFE-SAFETY", color: "#F87171", bg: "rgba(248,113,113,0.10)" },
  deficiency:  { text: "DEFICIENCY",  color: "#FFB347", bg: "rgba(255,179,71,0.10)" },
  observation: { text: "OBSERVATION", color: "#5AC8D8", bg: "rgba(90,200,216,0.10)" },
};

const OVERALL_BADGE = {
  hold:                 { text: "HOLD · LIFE-SAFETY OPEN",     color: "#F87171", bg: "rgba(248,113,113,0.12)" },
  corrections_required: { text: "CORRECTIONS REQUIRED",         color: "#FFB347", bg: "rgba(255,179,71,0.12)" },
  ready_for_lease:      { text: "READY FOR LEASE",              color: "#00DC78", bg: "rgba(0,220,120,0.12)" },
};

const NspireWalkthrough = ({ propertyAddress, entityId, inspector }) => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeZone, setActiveZone] = useState("");
  const [responses, setResponses] = useState({});   // key = `${zone_id}:${item_id}` → { status, notes, photo_url }
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [prefillNote, setPrefillNote] = useState("");
  const fileInputRefs = useRef({});

  // ─── Entity picker (institutional owner / GP for closeout packet) ─
  const [entities, setEntities] = useState([]);
  const [entitiesLoaded, setEntitiesLoaded] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(entityId || "");
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // ─── Handicap Accessibility field (baseline schema requirement · R12) ─
  const [handicapAccessibility, setHandicapAccessibility] = useState("unknown");

  // ─── Adaptive Closet capture (deep-wired · flows into closeout PDF) ─
  const [closetCaptures, setClosetCaptures] = useState([]);
  const [closetForm, setClosetForm] = useState({ closet_id: "", width_in: "", depth_in: "", height_in: "" });
  const [closetBusy, setClosetBusy] = useState(false);
  const [closetMsg, setClosetMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/nspire/entities`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setEntities(d.entities || []);
      } catch { /* silent */ }
      if (!cancelled) setEntitiesLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (entityId && !selectedEntity) setSelectedEntity(entityId);
  }, [entityId, selectedEntity]);

  const downloadClosePdf = async () => {
    if (!selectedEntity || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/closeout/${encodeURIComponent(selectedEntity)}/nspire.pdf`,
        { credentials: "include" }
      );
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MF-Resolute-Closeout-${selectedEntity}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setSaveResult({ error: String(e.message || e) });
    }
    setDownloadingPdf(false);
  };

  // ─── Load checklist ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/nspire/checklist`);
        if (!r.ok) throw new Error(`checklist ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        const z = d.zones || [];
        setZones(z);
        if (z[0]) setActiveZone(z[0].zone_id);
      } catch (e) {
        if (!cancelled) setLoadError(String(e.message || e));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── Prefill from latest walkthrough for this address ───────────
  useEffect(() => {
    if (!propertyAddress || !propertyAddress.trim()) return;
    let cancelled = false;
    (async () => {
      try {
        const q = encodeURIComponent(propertyAddress.trim());
        const r = await fetch(`${BACKEND_URL}/api/nspire/walkthrough/latest?property_address=${q}`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled || !d.walkthrough) return;
        const w = d.walkthrough;
        const map = {};
        (w.responses || []).forEach(x => {
          map[`${x.zone_id}:${x.item_id}`] = {
            status: x.status,
            notes: x.notes || "",
            photo_url: x.photo_url || "",
          };
        });
        setResponses(map);
        setPrefillNote(`Prefilled from last walkthrough · ${new Date(w.captured_at).toLocaleString()}`);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [propertyAddress]);

  // ─── Load Adaptive Closet captures for this address ─────────────
  useEffect(() => {
    if (!propertyAddress || !propertyAddress.trim()) { setClosetCaptures([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const q = encodeURIComponent(propertyAddress.trim());
        const r = await fetch(`${BACKEND_URL}/api/nspire/closet-captures?property_address=${q}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setClosetCaptures(d.captures || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [propertyAddress]);

  const captureCloset = async () => {
    if (!propertyAddress || !propertyAddress.trim()) { setClosetMsg("Enter an address above first."); return; }
    const w = parseFloat(closetForm.width_in), d = parseFloat(closetForm.depth_in), h = parseFloat(closetForm.height_in);
    if (!(w > 0 && d > 0 && h > 0)) { setClosetMsg("Width / depth / height must all be positive inches."); return; }
    setClosetBusy(true); setClosetMsg("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/nspire/closet-capture`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          property_address: propertyAddress.trim(),
          closet_id:        closetForm.closet_id.trim() || `closet-${Date.now()}`,
          width_in: w, depth_in: d, height_in: h,
          field_tags: [],
          preferred_tier: "clearance",
        }),
      });
      const dj = await r.json();
      if (!r.ok) throw new Error(dj.detail || "capture failed");
      setClosetCaptures(prev => [dj.capture, ...prev]);
      setClosetForm({ closet_id: "", width_in: "", depth_in: "", height_in: "" });
      const tot = dj.capture?.plan?.totals_usd || {};
      setClosetMsg(`Captured · saved $${(tot.savings_vs_retail || 0).toFixed(2)} vs retail · Wave ${(dj.capture?.plan?.wave_classification || {}).assigned_wave || "?"}`);
    } catch (e) {
      setClosetMsg(String(e.message || e));
    }
    setClosetBusy(false);
  };

  const setResp = (zoneId, itemId, patch) => {
    setResponses(prev => ({
      ...prev,
      [`${zoneId}:${itemId}`]: { ...(prev[`${zoneId}:${itemId}`] || {}), ...patch },
    }));
  };

  const totals = useMemo(() => {
    let total = 0, done = 0;
    zones.forEach(z => {
      z.items.forEach(it => {
        total += 1;
        const r = responses[`${z.zone_id}:${it.id}`];
        if (r && r.status) done += 1;
      });
    });
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [zones, responses]);

  const zoneProgress = (z) => {
    let done = 0;
    z.items.forEach(it => {
      const r = responses[`${z.zone_id}:${it.id}`];
      if (r && r.status) done += 1;
    });
    return { done, total: z.items.length };
  };

  // ─── Photo capture (small — base64 into responses) ──────────────
  const handlePhoto = async (zoneId, itemId, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setResp(zoneId, itemId, { photo_url: reader.result });
    reader.readAsDataURL(file);
  };

  // ─── Submit ─────────────────────────────────────────────────────
  const canSave = propertyAddress && propertyAddress.trim() && totals.done > 0;
  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const payload = {
        property_address: propertyAddress.trim(),
        entity_id: (selectedEntity || "").trim() || null,
        inspector: (inspector || "").trim() || null,
        handicap_accessibility: handicapAccessibility || "unknown",
        responses: Object.entries(responses)
          .filter(([, v]) => v && v.status)
          .map(([k, v]) => {
            const [zone_id, item_id] = k.split(":");
            return {
              zone_id, item_id,
              status: v.status,
              notes: v.notes || "",
              photo_url: v.photo_url || "",
            };
          }),
      };
      const r = await fetch(`${BACKEND_URL}/api/nspire/walkthrough`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Save failed");
      setSaveResult(d);
      // Auto-download closeout PDF the moment a life-safety fail is captured
      // so Shannon can forward evidence to the entity before leaving the unit.
      const overall = d?.walkthrough?.score?.overall_status;
      if (overall === "hold" && selectedEntity) {
        // Small delay so the user sees the status badge flip first.
        setTimeout(() => { downloadClosePdf(); }, 600);
      }
    } catch (e) {
      setSaveResult({ error: String(e.message || e) });
    }
    setSaving(false);
  };

  // ─── Empty / error states ───────────────────────────────────────
  if (loading) return <div style={{ color: "#7E92A8", padding: "1rem 0" }}>Loading NSPIRE checklist…</div>;
  if (loadError) return <div style={{ color: "#F87171", padding: "1rem 0" }}>Failed to load checklist: {loadError}</div>;
  if (!zones.length) return <div style={{ color: "#7E92A8" }}>No zones available.</div>;

  const zObj = zones.find(z => z.zone_id === activeZone) || zones[0];

  return (
    <div data-testid="nspire-walkthrough">
      {/* ─── Header: address bind + progress ─────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, flexWrap: "wrap", padding: "10px 14px",
        background: "rgba(0,255,255,0.04)", border: "1px solid rgba(0,255,255,0.15)",
        borderRadius: 10, marginBottom: 14,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.18em", fontWeight: 800 }}>
            &gt; NSPIRE_WALKTHROUGH · ATTACHED TO RENDER + PROCUREMENT
          </div>
          <div style={{ color: "#fff", fontSize: "0.92rem", fontWeight: 700, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {propertyAddress || <span style={{ color: "#FFB347" }}>Enter a property address above to bind this walkthrough</span>}
          </div>
          <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 2 }}>
            Rolls up into the corporate Resolute closeout packet automatically.
          </div>
        </div>
        <div style={{ minWidth: 160 }}>
          <div style={{ height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 999, overflow: "hidden" }}>
            <div data-testid="nspire-overall-bar" style={{ height: "100%", width: `${totals.pct}%`, background: "#00FFFF", transition: "width 0.3s" }} />
          </div>
          <div style={{ color: "#B4BEC8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.68rem", marginTop: 4 }}>
            {totals.done} / {totals.total} items evaluated
          </div>
        </div>
      </div>

      {/* ─── Entity picker + Closeout PDF download ────────────────── */}
      <div data-testid="nspire-entity-picker-row" style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
        padding: "10px 14px", marginBottom: 12,
        background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 10,
      }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.16em", marginBottom: 4 }}>
            INSTITUTIONAL ENTITY
          </div>
          <select
            data-testid="nspire-entity-select"
            value={selectedEntity}
            onChange={e => setSelectedEntity(e.target.value)}
            style={{
              width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
              border: "1px solid rgba(0,255,255,0.25)", borderRadius: 6,
              padding: "8px 10px", fontFamily: "'Inter', sans-serif",
              fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
            }}
          >
            <option value="">
              {entitiesLoaded
                ? (entities.length ? "— Select an institutional entity —" : "No entities on file yet")
                : "Loading entities…"}
            </option>
            {entities.map(e => (
              <option key={e.entity_id} value={e.entity_id}>
                {e.label}{e.address ? ` · ${e.address}` : ""}
              </option>
            ))}
          </select>
          <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: 4 }}>
            Walkthrough attaches to this entity&apos;s Resolute closeout packet.
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.16em", marginBottom: 4 }}>
            HANDICAP ACCESSIBILITY
          </div>
          <select
            data-testid="nspire-handicap-select"
            value={handicapAccessibility}
            onChange={e => setHandicapAccessibility(e.target.value)}
            style={{
              width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
              border: "1px solid rgba(0,255,255,0.25)", borderRadius: 6,
              padding: "8px 10px", fontFamily: "'Inter', sans-serif",
              fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
            }}
          >
            <option value="unknown">— Not yet assessed —</option>
            <option value="ada_certified">ADA / Section 504 Certified</option>
            <option value="partial">Partial (some units retrofit)</option>
            <option value="none">Non-Accessible</option>
          </select>
          <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: 4 }}>
            Baseline compliance field · rolls into closeout packet.
          </div>
        </div>
        <button
          data-testid="nspire-download-pdf-btn"
          onClick={downloadClosePdf}
          disabled={!selectedEntity || downloadingPdf}
          style={{
            background: selectedEntity ? "rgba(0,255,255,0.10)" : "rgba(255,255,255,0.03)",
            color: selectedEntity ? "#00FFFF" : "#7E92A8",
            border: `1px solid ${selectedEntity ? "rgba(0,255,255,0.45)" : "rgba(255,255,255,0.08)"}`,
            borderRadius: 8, padding: "10px 14px",
            fontFamily: "'Inter', sans-serif", fontSize: "0.82rem", fontWeight: 700,
            cursor: selectedEntity && !downloadingPdf ? "pointer" : "not-allowed",
            whiteSpace: "nowrap",
          }}
        >
          {downloadingPdf ? "Preparing PDF…" : "⬇ Download Resolute Closeout PDF"}
        </button>
      </div>

      {/* ─── Adaptive Closet capture (deep-wired · flows into closeout) ─ */}
      <div data-testid="nspire-closet-capture-panel" style={{
        padding: "10px 14px", marginBottom: 12,
        background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.20)",
        borderRadius: 10,
      }}>
        <div style={{ color: "#A78BFA", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.16em", marginBottom: 6, fontWeight: 800 }}>
          &gt; ADAPTIVE_CLOSET · DIMENSION-DRIVEN · ANTI-LOCKDOWN
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <input
            data-testid="closet-id-input"
            placeholder="Closet ID (e.g. unit-A-master)"
            value={closetForm.closet_id}
            onChange={e => setClosetForm(f => ({ ...f, closet_id: e.target.value }))}
            style={{ flex: "1 1 180px", background: "rgba(2,10,31,0.6)", color: "#fff", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 6, padding: "7px 9px", fontSize: "0.82rem" }}
          />
          <input
            data-testid="closet-width-input"
            type="number" min="0" step="0.5" placeholder="Width in"
            value={closetForm.width_in}
            onChange={e => setClosetForm(f => ({ ...f, width_in: e.target.value }))}
            style={{ width: 90, background: "rgba(2,10,31,0.6)", color: "#fff", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 6, padding: "7px 9px", fontSize: "0.82rem" }}
          />
          <input
            data-testid="closet-depth-input"
            type="number" min="0" step="0.5" placeholder="Depth in"
            value={closetForm.depth_in}
            onChange={e => setClosetForm(f => ({ ...f, depth_in: e.target.value }))}
            style={{ width: 90, background: "rgba(2,10,31,0.6)", color: "#fff", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 6, padding: "7px 9px", fontSize: "0.82rem" }}
          />
          <input
            data-testid="closet-height-input"
            type="number" min="0" step="0.5" placeholder="Height in"
            value={closetForm.height_in}
            onChange={e => setClosetForm(f => ({ ...f, height_in: e.target.value }))}
            style={{ width: 90, background: "rgba(2,10,31,0.6)", color: "#fff", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 6, padding: "7px 9px", fontSize: "0.82rem" }}
          />
          <button
            data-testid="closet-capture-btn"
            onClick={captureCloset}
            disabled={closetBusy || !propertyAddress}
            style={{
              background: propertyAddress ? "rgba(167,139,250,0.14)" : "rgba(255,255,255,0.03)",
              color: propertyAddress ? "#A78BFA" : "#7E92A8",
              border: `1px solid ${propertyAddress ? "rgba(167,139,250,0.45)" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 8, padding: "8px 14px",
              fontFamily: "'Inter', sans-serif", fontSize: "0.82rem", fontWeight: 700,
              cursor: propertyAddress && !closetBusy ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
            }}
          >
            {closetBusy ? "Planning…" : "+ Capture Closet"}
          </button>
        </div>
        {closetMsg && (
          <div data-testid="closet-capture-msg" style={{ color: "#A78BFA", fontSize: "0.72rem", marginTop: 6, fontStyle: "italic" }}>
            {closetMsg}
          </div>
        )}
        {closetCaptures.length > 0 && (
          <div data-testid="closet-captures-list" style={{ marginTop: 8, borderTop: "1px solid rgba(167,139,250,0.15)", paddingTop: 6 }}>
            <div style={{ color: "#B4BEC8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.14em", marginBottom: 4 }}>
              {closetCaptures.length} CAPTURE{closetCaptures.length > 1 ? "S" : ""} · AUTO-FLOWS INTO CLOSEOUT PDF
            </div>
            {closetCaptures.slice(0, 4).map((c, i) => {
              const t = c?.plan?.totals_usd || {};
              const w = c?.plan?.wave_classification?.wave_name || "";
              return (
                <div key={c.capture_id || i} style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "#E7ECF3", fontSize: "0.76rem", padding: "3px 0" }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#A78BFA" }}>{c.closet_id}</span>
                  <span style={{ color: "#7E92A8" }}>{w}</span>
                  <span style={{ color: "#00DC78" }}>−${(t.savings_vs_retail || 0).toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {prefillNote && (
        <div data-testid="nspire-prefill-note" style={{ color: "#5AC8D8", fontSize: "0.75rem", marginBottom: 10, fontStyle: "italic" }}>
          {prefillNote}
        </div>
      )}

      {/* ─── Zone tabs ────────────────────────────────────────────── */}
      <div data-testid="nspire-zone-tabs" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {zones.map(z => {
          const p = zoneProgress(z);
          const active = z.zone_id === activeZone;
          const complete = p.done === p.total;
          return (
            <button
              key={z.zone_id}
              data-testid={`nspire-zone-${z.zone_id}`}
              onClick={() => setActiveZone(z.zone_id)}
              style={{
                background: active ? "rgba(0,255,255,0.12)" : "rgba(255,255,255,0.03)",
                color: active ? "#00FFFF" : "#B4BEC8",
                border: `1px solid ${active ? "rgba(0,255,255,0.55)" : "rgba(255,255,255,0.08)"}`,
                borderRadius: 8, padding: "8px 12px", cursor: "pointer",
                fontFamily: "'Inter', sans-serif", fontSize: "0.82rem", fontWeight: 700,
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <span>{z.label}</span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem",
                color: complete ? "#00DC78" : "#7E92A8",
                background: complete ? "rgba(0,220,120,0.10)" : "transparent",
                padding: "2px 6px", borderRadius: 4,
              }}>
                {p.done}/{p.total}
              </span>
            </button>
          );
        })}
      </div>

      {/* ─── Item list for active zone ────────────────────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid={`nspire-items-${zObj.zone_id}`}>
        {zObj.items.map(it => {
          const r = responses[`${zObj.zone_id}:${it.id}`] || {};
          const sev = SEVERITY_BADGE[it.severity] || SEVERITY_BADGE.deficiency;
          return (
            <div key={it.id} data-testid={`nspire-item-${it.id}`}
                 style={{
                   background: "rgba(255,255,255,0.02)",
                   border: `1px solid ${r.status === "fail" ? "rgba(248,113,113,0.4)" : "rgba(255,255,255,0.06)"}`,
                   borderRadius: 10, padding: "12px 14px",
                 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#fff", fontSize: "0.92rem", fontWeight: 700, lineHeight: 1.35 }}>{it.label}</div>
                  <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 3 }}>{it.code_ref}</div>
                </div>
                <span style={{
                  color: sev.color, background: sev.bg, border: `1px solid ${sev.color}44`,
                  fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem",
                  letterSpacing: "0.1em", padding: "2px 6px", borderRadius: 4,
                  whiteSpace: "nowrap", flexShrink: 0,
                }}>{sev.text}</span>
              </div>

              {/* Status radio */}
              <div style={{ display: "flex", gap: 6, marginBottom: r.status ? 10 : 0 }}>
                {STATUS.map(s => {
                  const on = r.status === s;
                  return (
                    <button
                      key={s}
                      data-testid={`nspire-status-${it.id}-${s}`}
                      onClick={() => setResp(zObj.zone_id, it.id, { status: s })}
                      style={{
                        flex: 1, background: on ? STATUS_COLOR[s] : "rgba(255,255,255,0.03)",
                        color: on ? "#030B1A" : "#B4BEC8",
                        border: `1px solid ${on ? STATUS_COLOR[s] : "rgba(255,255,255,0.08)"}`,
                        borderRadius: 6, padding: "6px 8px",
                        fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: "0.78rem",
                        cursor: "pointer", letterSpacing: "0.02em",
                      }}
                    >
                      {STATUS_LABEL[s]}
                    </button>
                  );
                })}
              </div>

              {/* Notes + photo appear once status is set */}
              {r.status && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input
                    data-testid={`nspire-notes-${it.id}`}
                    type="text"
                    placeholder={r.status === "fail" ? "What's wrong? Where? (optional)" : "Notes (optional)"}
                    value={r.notes || ""}
                    onChange={e => setResp(zObj.zone_id, it.id, { notes: e.target.value })}
                    style={{
                      flex: 1, minWidth: 180,
                      background: "rgba(2,10,31,0.5)", color: "#fff",
                      border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6,
                      padding: "6px 10px", fontFamily: "'Inter', sans-serif", fontSize: "0.82rem",
                    }}
                  />
                  <button
                    data-testid={`nspire-photo-btn-${it.id}`}
                    onClick={() => fileInputRefs.current[it.id]?.click()}
                    style={{
                      background: r.photo_url ? "rgba(0,220,120,0.10)" : "rgba(255,255,255,0.03)",
                      color: r.photo_url ? "#00DC78" : "#B4BEC8",
                      border: `1px solid ${r.photo_url ? "rgba(0,220,120,0.35)" : "rgba(255,255,255,0.08)"}`,
                      borderRadius: 6, padding: "6px 10px", cursor: "pointer",
                      fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", fontWeight: 700,
                    }}
                  >
                    {r.photo_url ? "✓ Photo attached" : "📷 Attach photo"}
                  </button>
                  <input
                    ref={el => { fileInputRefs.current[it.id] = el; }}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={e => handlePhoto(zObj.zone_id, it.id, e.target.files?.[0])}
                    style={{ display: "none" }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── Save + result ────────────────────────────────────────── */}
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <button
          data-testid="nspire-save-btn"
          onClick={submit}
          disabled={!canSave || saving}
          style={{
            background: canSave && !saving ? "#00FFFF" : "rgba(255,255,255,0.08)",
            color: canSave && !saving ? "#030B1A" : "#7E92A8",
            border: "none", borderRadius: 8, padding: "12px 18px",
            fontFamily: "'Inter', sans-serif", fontSize: "0.9rem", fontWeight: 800,
            letterSpacing: "0.02em",
            cursor: canSave && !saving ? "pointer" : "not-allowed",
          }}
        >
          {saving
            ? "Attaching to closeout packet…"
            : "Save & attach to corporate closeout packet"}
        </button>

        {saveResult && saveResult.error && (
          <div data-testid="nspire-save-error" style={{
            padding: "10px 12px", background: "rgba(248,113,113,0.08)",
            border: "1px solid rgba(248,113,113,0.35)", borderRadius: 8,
            color: "#F87171", fontSize: "0.82rem",
          }}>
            {saveResult.error}
          </div>
        )}

        {saveResult && !saveResult.error && (() => {
          const s = saveResult.walkthrough?.score || {};
          const badge = OVERALL_BADGE[s.overall_status] || OVERALL_BADGE.ready_for_lease;
          return (
            <div data-testid="nspire-save-result" style={{
              padding: "14px", background: "rgba(0,255,255,0.03)",
              border: "1px solid rgba(0,255,255,0.15)", borderRadius: 10,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                <span style={{
                  color: badge.color, background: badge.bg, border: `1px solid ${badge.color}55`,
                  fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem",
                  letterSpacing: "0.14em", padding: "4px 10px", borderRadius: 4, fontWeight: 800,
                }}>{badge.text}</span>
                <span style={{ color: "#fff", fontSize: "0.85rem" }}>
                  {s.passed} passed · {s.failed_count} failed · {s.na} N/A
                </span>
              </div>
              <div style={{ color: "#B4BEC8", fontSize: "0.78rem", lineHeight: 1.5 }}>
                Walkthrough attached to <b style={{ color: "#00FFFF" }}>{saveResult.walkthrough?.property_address}</b>
                {saveResult.walkthrough?.entity_id
                  ? <> and routed into the corporate closeout packet for <b>{saveResult.walkthrough.entity_id}</b>.</>
                  : <>. Bind an entity from the Dashboard to route into a specific closeout packet.</>}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default NspireWalkthrough;
